import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ReactComponent as AddIcon } from './add.svg';
import { ReactComponent as EditIcon } from './edit.svg';
import styled from 'styled-components/macro';
import { Button, Space } from '@gr-monorepo/common/ui';
import Modal from '@gr-monorepo/common/ui/Modal';
import Input from '@gr-monorepo/common/ui/Input/Input';
import Tooltip from 'features/shared/Tooltip';
import { useClickStreamContext } from 'features/shared/ClickStream';

const ButtonLink = styled.div<{ align?: string }>`
  color: #12b7e7;
  cursor: pointer;
  text-align: ${(props) => props.align || 'center'};
`;
const Header = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 34px;
`;
const TextRow = styled.div`
  font-size: 20px;
  line-height: 28px;
`;
const Row = styled.div`
  display: flex;
  font-size: 20px;
  line-height: 28px;
  align-items: center;
  justify-content: center;
`;
const Content = styled.div`
  width: 438px;
  background: #ffffff;
  border-radius: 16px;
  padding: 40px;
  box-sizing: content-box;
  position: relative;
`;
const Edit = styled(EditIcon)`
  margin: 0 22px -2px 8px;
`;
export const StudentCountCell = (props: {
  id?: number;
  schoolName?: string;
  schoolLocation?: string;
  subjectName?: string;
  studentCount?: number | null;
  setStudentCount: (id: number | null, studentCount: number) => void;
}) => {
  const { logPageEvent } = useClickStreamContext();

  const pending = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [value, setValue] = useState(typeof props.studentCount === 'number' ? props.studentCount + '' : '');
  const [error, setError] = useState('');

  const text = useMemo(() => {
    return [props.schoolLocation, props.subjectName].filter((el) => el).join(', ');
  }, [props.schoolLocation, props.subjectName]);

  const handleClose = () => {
    setShowModal(false);
    setError('');
  };

  const handleClick = async () => {
    if (pending.current) return;
    pending.current = true;
    if (!value) {
      setError('Укажите количество учеников');
      pending.current = false;

      return;
    }
    await props.setStudentCount(props.id || null, +value);
    logPageEvent({
      eventType: 'schools_active_school_send_student_count',
      eventInfo: 'Школы|Активные школы: Отправить количество учеников в школе',
    });
    handleClose();
    pending.current = false;
  };

  useEffect(() => {
    setValue(typeof props.studentCount === 'number' ? props.studentCount + '' : '');
  }, [showModal]);

  return (
    <>
      <Tooltip
        title={props.studentCount ? 'Нажмите, чтобы отредактировать' : 'Нажмите, чтобы добавить информацию'}
        arrow
        placement="top"
        minWidth={211}
        maxWidth={props.studentCount ? 330 : 250}
      >
        <ButtonLink
          onClick={() => {
            logPageEvent({
              eventType: props.studentCount
                ? 'schools_active_school_edit_student_count'
                : 'schools_active_school_add_student_count',
              eventInfo: props.studentCount
                ? 'Школы|Активные школы: Изменение количества учеников в школе'
                : 'Школы|Активные школы: Добавление количества учеников в школе',
            });
            setShowModal(true);
          }}
          align={props.studentCount ? 'right' : 'center'}
        >
          {props.studentCount ? (
            <>
              {props.studentCount}
              <Edit />
            </>
          ) : (
            <AddIcon />
          )}
        </ButtonLink>
      </Tooltip>
      {showModal && (
        <Modal
          customModalWindow={Content}
          onClose={() => {
            handleClose();
            logPageEvent({
              eventType: 'schools_active_school_close_modal_student_count',
              eventInfo: 'Школы|Активные школы: Закрыл модалку добавления учеников в школе',
            });
          }}
        >
          <Header>
            {typeof props.studentCount === 'number'
              ? 'Изменение количества учеников'
              : 'Добавление количества учеников'}
          </Header>
          <Space size={16} axis={'vertical'} />

          <TextRow>{text}</TextRow>

          {props.schoolName && (
            <>
              <Space size={16} axis={'vertical'} />
              <TextRow>{props.schoolName}</TextRow>
            </>
          )}
          <Space size={28} axis={'vertical'} />

          <Row>
            <div style={{ flex: '0 0 auto' }}>Всего учеников</div> <Space size={12} />
            <Input
              value={value}
              onChange={(newValue: string) => {
                if (error) setError('');
                setValue(newValue);
              }}
              placeholder={props.id ? 'Количество учеников в школе' : 'Количество учеников'}
              error={!!error}
              errorText={error}
              inputProps={{
                autoComplete: 'off',
              }}
              mask={'0000000'}
              style={{ width: '100%' }}
            />
          </Row>
          <Space size={64} axis={'vertical'} />
          <Row>
            <Button
              onClick={() => {
                handleClose();
                logPageEvent({
                  eventType: 'schools_active_school_close_modal_student_count',
                  eventInfo: 'Школы|Активные школы: Закрыл модалку добавления учеников в школе',
                });
              }}
              color={'azure'}
            >
              отменить
            </Button>
            <Space size={24} />

            <Button onClick={handleClick}>сохранить</Button>
          </Row>
        </Modal>
      )}
    </>
  );
};
